<script setup>
import Button from "@/features/Button.vue";
import { computed, watch } from "vue";
import InlineSvg from "@/features/InlineSvg.vue";
import { MONTHS } from "@/scripts/constants";
import { useDataDeletionRow } from "@/routes/DataDeletion/composables/useDataDeletionRow";
import DataRemovalRequestsRowDetailsPeopleList from "@/routes/DataDeletion/components/RequestRows/DataRemovalRequestsRowDetailsPeopleList.vue";
import { formatAddress } from "@/scripts/format";
import BaseText from "@/library/BaseText.vue";

// Use our shared composable for common functionality
const {
  iframeRef,
  rowDetailsRef,
  state,
  renderMarkdown,
  resizeIframe,
  setupResizeWatch,
  isNameInSupportedList,
  onLinkToSupportPage,
} = useDataDeletionRow();

// Set up the iframe resize watcher
setupResizeWatch();

const props = defineProps({
  family: {
    type: Object,
    required: true,
  },
  date: {
    type: String,
    required: true,
  },
  brokers: {
    type: Array,
    default: null,
  },
});

// Computed properties derived from the family prop
const name = computed(() => props.family?.name || "");
const count = computed(() => props.family?.brokers?.length || 0);
const opened = computed(() => props.family?.opened || false);
const showManualRemovalSteps = computed(
  () => props.family?.showManualRemovalSteps || false
);
const instructions = computed(() => props.family?.removal_steps || "");

// Use either the directly provided brokers or those from the family
const brokersToUse = computed(
  () => props.brokers || props.family?.brokers || []
);
const emit = defineEmits([
  "openOperatorModal",
  "toggleOpen",
  "updateActionRequiredFamilies",
]);

// Use the shared renderMarkdown function
const markdownToHtml = computed(() => renderMarkdown(instructions.value));

// Watch to resize iframe after animation completes
watch(showManualRemovalSteps, (newValue) => {
  if (newValue) {
    // Small delay to allow animation to complete before resizing iframe
    setTimeout(resizeIframe, 350);
  }
});

const showSupportLink = computed(() => isNameInSupportedList(name.value));

function handleSupportLinkClick() {
  onLinkToSupportPage(name.value);
}

const recordsFormatted = computed(() => {
  return brokersToUse.value?.flatMap((broker) => {
    const recordList = broker.records || [];
    return recordList.map((record) => {
      if (record.pii) {
        let parsedPII = {};
        try {
          parsedPII = JSON.parse(record.pii);
          parsedPII.addresses = parsedPII.addresses.map((address) => {
            return formatAddress(address);
          });
        } catch (err) {
          console.warn("Error parsing record PII:", err);
          return record;
        }
        return {
          ...record,
          pii: parsedPII,
        };
      }
      return record;
    });
  });
});

const sentDate = computed(() => {
  const brokerWithSentAt = brokersToUse.value?.find((broker) => broker.sent_at);
  if (brokerWithSentAt) {
    const sentAtDate = new Date(brokerWithSentAt.sent_at);
    return ` ${
      MONTHS[sentAtDate.getMonth()]
    } ${sentAtDate.getDate()}, ${sentAtDate.getFullYear()}`;
  }
  return "—";
});

const completedDate = computed(() => {
  const brokerWithCompletedAt = brokersToUse.value?.find(
    (broker) => broker.completed_at
  );
  if (brokerWithCompletedAt) {
    const completedAtDate = new Date(brokerWithCompletedAt.completed_at);
    return ` ${
      MONTHS[completedAtDate.getMonth()]
    } ${completedAtDate.getDate()}, ${completedAtDate.getFullYear()}`;
  }
  return "—";
});

const estimatedTime = computed(() => {
  const brokerWithCompletedAt = brokersToUse.value?.find(
    (broker) => broker.completed_at
  );
  if (brokerWithCompletedAt) {
    const completedAtDate = new Date(brokerWithCompletedAt.completed_at);
    const today = new Date();
    if (today > completedAtDate) {
      return "Complete";
    }
  }
  const brokerWithEstimatedDays = brokersToUse.value?.find(
    (broker) => typeof broker.estimated_days_to_remove_records === "number"
  );
  if (brokerWithEstimatedDays) {
    const days = brokerWithEstimatedDays.estimated_days_to_remove_records;
    return `${days} ${days === 1 ? "day" : "days"}`;
  }
  return "—";
});

function toggleManualSteps() {
  const familyUpdate = {
    ...props.family,
    showManualRemovalSteps: !props.family.showManualRemovalSteps,
  };

  emit("updateActionRequiredFamilies", familyUpdate);
}

// Simplified action text since this component is only used when operator is enabled
const actionButtonText = "Start Removal";
const shortActionButtonText = "Done";
const actionStatusText = "Manual Start Needed";
</script>
<template>
  <div
    class="action-row-wrapper"
    :class="{ opened: opened }"
    @click.stop="emit('toggleOpen')"
  >
    <div class="action-row">
      <div class="name action-row-item">
        <BaseText
          variant="headline-6-medium"
          class="icon"
        >
          {{ opened ? "-" : "+" }}
        </BaseText>
        <BaseText
          variant="headline-6-medium"
          class="text"
        >
          {{ `${name}${count ? " (" + count + ")" : ""}` }}
        </BaseText>
      </div>
      <div class="review-text action-row-item">
        <InlineSvg name="rectangle-and-hand-point-up-left-filled" />
        <BaseText variant="headline-6-medium">{{ actionStatusText }}</BaseText>
      </div>
      <div class="done-button action-row-item">
        <Button
          type="secondary"
          size="md"
          class="long-button"
          fullWidth
          @click.stop="emit('openOperatorModal')"
        >
          <template #icon>
            <InlineSvg name="wand-2" />
          </template>
          {{ actionButtonText }}
        </Button>
        <Button
          type="secondary"
          size="full"
          class="med-button"
          fullWidth
          @click.stop="emit('openOperatorModal')"
        >
          <template #icon>
            <InlineSvg name="wand-2" />
          </template>
          {{ actionButtonText }}
        </Button>
        <Button
          type="secondary"
          size="full"
          class="short-button"
          fullWidth
          @click.stop="emit('openOperatorModal')"
        >
          <template #icon>
            <InlineSvg name="wand-2" />
          </template>
          {{ shortActionButtonText }}
        </Button>
      </div>
    </div>
    <div
      class="action-row-details-wrapper"
      :class="{ opened: opened }"
    >
      <div
        ref="rowDetailsRef"
        class="action-row-details"
        :class="{ opened: opened }"
      >
        <BaseText
          variant="headline-6-medium"
          class="disclaimer"
          as="div"
        >
          Press {{ actionButtonText }} to begin the process with {{ name }} -
          Cloaked will automatically take care of the rest.
        </BaseText>
        <div class="time-wrapper-row">
          <div class="time-wrapper">
            <BaseText
              as="h1"
              variant="body-small-medium"
            >
              First Found On
            </BaseText>
            <BaseText
              as="p"
              variant="caption-semibold"
            >
              {{ sentDate }}
            </BaseText>
          </div>
          <div class="time-wrapper">
            <BaseText
              as="h1"
              variant="body-small-medium"
            >
              Completed on
            </BaseText>
            <BaseText
              as="p"
              variant="caption-semibold"
            >
              {{ completedDate }}
            </BaseText>
          </div>
          <div class="time-wrapper">
            <BaseText
              as="h1"
              variant="body-small-medium"
            >
              Estimated completion
            </BaseText>
            <BaseText
              as="p"
              variant="caption-semibold"
            >
              {{ estimatedTime }}
            </BaseText>
          </div>
        </div>
        <DataRemovalRequestsRowDetailsPeopleList
          :brokerName="name"
          :records="recordsFormatted"
          :status="opened ? 'open' : 'closed'"
          svgIcon="scanning"
        />
        <div
          v-if="instructions"
          class="button-container"
        >
          <Button
            type="secondary"
            size="md"
            class="collapsible-button"
            @click.stop="toggleManualSteps"
          >
            <BaseText variant="body-small-medium">
              {{ showManualRemovalSteps ? "Hide" : "Show" }} steps to manually
              remove your records from {{ name }}
              <BaseText
                variant="body-small-medium"
                as="span"
                class="collapsible-icon"
              >
                {{ showManualRemovalSteps ? "-" : "+" }}
              </BaseText>
            </BaseText>
          </Button>
        </div>
        <div
          v-if="instructions"
          class="collapsible-steps-wrapper"
          :class="{ opened: showManualRemovalSteps }"
        >
          <div class="collapsible-content">
            <BaseText
              as="h3"
              variant="headline-4-bold"
            >
              How to remove your records from {{ name }}
            </BaseText>

            <iframe
              ref="iframeRef"
              class="instructions"
              :srcdoc="markdownToHtml"
              :style="`height: ${state.iframeHeight}px; width: ${state.iframeWidth}px;`"
              frameborder="0"
              sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
              @load="resizeIframe"
            />
            <BaseText
              v-if="showSupportLink"
              as="h3"
              variant="headline-4-bold"
            >
              {{ "Need more help removing your information? Click " }}
              <span
                class="link-text"
                @click="handleSupportLinkClick"
              >
                {{ "here" }}
              </span>
              {{ " to access a step by step walkthrough." }}
            </BaseText>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<style scoped lang="scss">
/* Only keep specific styles unique to UiActionRowOperator */
.action-row-details {
  &.opened {
    margin-top: 25px;
  }

  .disclaimer {
    background-color: $color-warning-light;
    color: $color-primary-100-light;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 24px;
    width: 100%;
    border-radius: 16px;
  }

  .time-wrapper-row {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    padding-top: 32px;

    .time-wrapper {
      width: calc((100% + 50px) / 3);

      &:first-of-type {
        width: calc(((100% + 50px) / 3) - 24px);
      }

      &:last-of-type {
        width: calc(((100% + 50px) / 3) - 24px);
      }
    }
  }

  .collapsible-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    cursor: pointer;
  }

  .collapsible-steps-wrapper {
    display: grid;
    grid-template-rows: 0fr;
    opacity: 0;
    width: 100%;
    transition:
      grid-template-rows 0.3s ease-out,
      opacity 0.3s ease-out;

    &.opened {
      grid-template-rows: 1fr;
      opacity: 1;
      margin-top: 16px;
    }

    .collapsible-content {
      overflow: hidden;
    }
  }

  .button-container {
    display: flex;
    justify-content: center;
    margin-top: 24px;
  }

  .collapsible-button {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: fit-content;

    .collapsible-icon {
      margin-left: 12px;
    }
  }

  ol {
    list-style: none;
    display: flex;
    flex-direction: column;
    gap: 8px;
    width: 100%;

    li {
      display: flex;
      flex-direction: row;
      gap: 15px;
      align-items: center;
      justify-content: flex-start;

      .list-rank {
        background-color: $color-primary-100;
        border-radius: 50%;
        color: $color-primary-1;
        height: 18px;
        width: 18px;
        flex-shrink: 0;
        display: flex;
        justify-content: center;
        align-items: center;
      }
    }
  }
}
</style>
